import type {
  ControlNode as ControlNodeType,
  Port,
} from "../../../../util/src/blueprint/types.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import React from "react";

export interface ControlRepeatNodeProps {
  node: ControlNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
}

const ControlRepeatNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
}: ControlRepeatNodeProps) => {
  const behaviorDico = dico().behavior;
  const valuePort = node.ports.find((port) => port.id === "value");
  const countPort = node.ports.find((port) => port.id === "count");
  const bodyPort = node.ports.find((port) => port.id === "body");
  const loopIndexPort = node.ports.find((port) => port.id === "loopIndex");
  const donePort = node.ports.find((port) => port.id === "done");
  if (!valuePort || !countPort || !bodyPort || !loopIndexPort || !donePort) {
    return null;
  }

  return (
    <div
      style={{
        minWidth: 280,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background:
            "linear-gradient(180deg, #93c5fd 0%, #3b82f6 55%, #1d4ed8 100%)",
        }}
      >
        {behaviorDico.controls.repeat}
      </div>
      <div style={{ display: "flex", alignItems: "stretch", padding: "6px 0" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <PortHandle
            node={node}
            port={valuePort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, valuePort)}
            onMouseUp={() => onPortMouseUp?.(node.id, valuePort)}
            isConnected={isPortConnected?.(node.id, valuePort.id) ?? false}
          />
          <PortHandle
            node={node}
            port={countPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, countPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, countPort)}
            showValueInput
            onValueChange={(value) =>
              onPortValueChange?.(node.id, countPort.id, value)
            }
            isConnected={isPortConnected?.(node.id, countPort.id) ?? false}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <PortHandle
            node={node}
            port={bodyPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, bodyPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, bodyPort)}
            isConnected={isPortConnected?.(node.id, bodyPort.id) ?? false}
          />
          <PortHandle
            node={node}
            port={loopIndexPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, loopIndexPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, loopIndexPort)}
            isConnected={isPortConnected?.(node.id, loopIndexPort.id) ?? false}
          />
          <PortHandle
            node={node}
            port={donePort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, donePort)}
            onMouseUp={() => onPortMouseUp?.(node.id, donePort)}
            isConnected={isPortConnected?.(node.id, donePort.id) ?? false}
          />
        </div>
      </div>
    </div>
  );
};

export const ControlRepeatNode = React.memo(ControlRepeatNodeComponent);
